import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  IconButton,
  Tab,
  Tabs,
  Chip,
  Card,
  useMediaQuery,
} from "@mui/material";
import {
  Book,
  ArrowLeft,
  Gavel,
  Users,
  FileText,
  Scale,
  Library,
  User,
  FileSpreadsheet,
  Link2,
} from "lucide-react";
import { toast } from "react-toastify";
import { parseCitationNames } from "../utils/HelperFunction/textFormatting";

import { fetchMaxims, findCaseSummary } from "../actions/searchActions";
import CaseDetailCard from "./CaseSearch/CaseDetailCard";

import CaseDetailJudgementTab from "./CaseSearch/CaseDetailJudgementTab";
import CaseDetailAdvocates from "./CaseSearch/CaseDetailAdvocates";
import CaseDetailStatutes from "./CaseSearch/CaseDetailStatutes";
import CaseDetailMaxims from "./CaseSearch/CaseDetailMaxims";
import CaseDetailOverview from "./CaseSearch/CaseDetailOverview";
import CaseDetailSummary from "./CaseSearch/CaseDetailSummary";
import SearchInputField from "./UI/SearchInputField";

const formatCaseTitle = (title) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(title, "text/html");

  const formatTextContent = (text) => {
    return text
      .split("Honorable")[0]
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const walkNodes = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        child.nodeValue = formatTextContent(child.nodeValue);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        walkNodes(child);
      }
    });
  };

  walkNodes(doc.body);

  return doc.body.innerHTML;
};

const TabIcon = ({ icon, bgColor, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      borderRadius: "6px",
      bgcolor: bgColor,
      color: color,
      mb: 0.25,
      mr: 0.25,
    }}
  >
    {icon}
  </Box>
);

const tabsData = [
  {
    label: "Overview",
    icon: <FileText size={14} />,
    bgColor: "#EFF6FF",
    color: "#2563EB",
  },
  {
    label: "Judgment",
    icon: <Gavel size={14} />,
    bgColor: "#FEF3C7",
    color: "#D97706",
  },
  {
    label: "Statutes & Citations",
    icon: <Library size={14} />,
    bgColor: "#ECFDF5",
    color: "#059669",
  },
  {
    label: "Advocates",
    icon: <User size={14} />,
    bgColor: "#F5F3FF",
    color: "#7C3AED",
  },
  {
    label: "Detailed Summary",
    icon: <FileSpreadsheet size={14} />,
    bgColor: "#FEE2E2",
    color: "#DC2626",
  },
  {
    label: "Maxims & Terms",
    icon: <Book size={14} />,
    bgColor: "#F0FDF4",
    color: "#16A34A",
  },
];

const CaseDetail = ({ closeOnBack }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const caseDetail = useSelector((state) => state.caseDetail);
  const { caseItem } = caseDetail;

  const [searchTerm, setSearchTerm] = useState("");
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [totalMatches, setTotalMatches] = React.useState(0);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [maxims, setMaxims] = useState({});

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/app/searchResults/${caseItem?.Bookmark_ID}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  useEffect(() => {
    const handleFetchMaxims = async () => {
      try {
        setLoading(true);
        const data = await fetchMaxims(caseItem?.Bookmark_ID);
        setMaxims(data?.Results);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to fetch maxims:", error);
        }
      } finally {
        setLoading(false);
      }
    };
    handleFetchMaxims();
  }, [caseItem?.Bookmark_ID]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: "100%" },
        width: "100%",
        bgcolor: "#F1F5F9",
        marginTop: "20px",
        marginLeft: { xs: 0, md: "30px" },
        overflowX: { xs: "unset", md: "hidden" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          boxSizing: "border-box",
          bgcolor: "#fff",
          borderBottom: "1px solid #E2E8F0",
          height: "60px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              fontFamily: "Inter, sans-serif",
            }}
          >
            <IconButton
              onClick={closeOnBack}
              sx={{
                color: "#2563EB",
                p: 0.75,
                borderRadius: "8px",
                bgcolor: "#EFF6FF",
                "&:hover": {
                  bgcolor: "#DBEAFE",
                  transform: "translateX(-2px)",
                },
                transition: "all 0.2s ease",
                minWidth: "32px",
                height: "32px",
              }}
            >
              <ArrowLeft size={16} />
            </IconButton>
            <IconButton
              onClick={handleCopyLink}
              title="Copy Link"
              sx={{
                color: "#2563EB",
                p: 0.75,
                borderRadius: "8px",
                bgcolor: "#EFF6FF",
                "&:hover": {
                  bgcolor: "#DBEAFE",
                },
                transition: "all 0.2s ease",
                minWidth: "32px",
                height: "32px",
              }}
            >
              <Link2 size={16} />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 2,
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: 600,
                  color: "#1E293B",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                  fontFamily: "Inter, sans-serif",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {formatCaseTitle(caseItem?.Title) || "Case Details"}
              </Typography>
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {parseCitationNames(caseItem?.Citation_Name).map(
                    (citation, index) => (
                      <Chip
                        key={index}
                        label={citation}
                        size="small"
                        sx={{
                          height: "20px",
                          fontSize: "12px",
                          backgroundColor: "#F8FAFC",
                          color: "#475569",
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "#F1F5F9",
                            borderColor: "#CBD5E1",
                          },
                        }}
                      />
                    ),
                  )}
                </Box>
              )}
            </Box>
          </Box>
          {(activeTab === 1 || activeTab === 4) && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 1.5,
                width: "25%",
              }}
            >
              <SearchInputField
                searchTerm={searchTerm}
                onSearch={handleSearchChange}
                currentMatch={currentMatch}
                setCurrentMatch={setCurrentMatch}
                totalMatches={totalMatches}
              />
            </Box>
          )}
        </Box>
      </Box>
      {/* Content Container */}
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          flexDirection: "row",
          bgcolor: "#F8FAFC",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {/* Left Sidebar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", md: "260px" },
            height: { xs: "auto", md: "100%" },
            overflowY: { xs: "visible", md: "auto" },
            boxSizing: "border-box",
            p: 1,
            gap: 1,
            flexShrink: 0,
          }}
        >
          {/* Case Information Card */}
          <CaseDetailCard
            title="Case Information"
            icon={Gavel}
            iconBgColor="#EFF6FF"
            iconColor="#2563EB"
            borderColor="#E2E8F0"
            hoverBorderColor="#2563EB"
            caseItem={caseItem}
          />

          {/* Bench Composition Card */}
          <CaseDetailCard
            title="Bench Composition"
            icon={Users}
            iconBgColor="#ECFDF5"
            iconColor="#059669"
            borderColor="#E2E8F0"
            hoverBorderColor="#059669"
            judges={caseItem?.Judge_Name}
          />
        </Box>

        {/* Tabs Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: { xs: "none", md: 1 },
            width: { xs: "100%", md: "auto" },
            p: 1,
            minHeight: { xs: "unset", md: 0 },
            boxSizing: "border-box",
          }}
        >
          <Card
            elevation={0}
            sx={{
              bgcolor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              height: { xs: "auto", md: "95%" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: "1px solid #E2E8F0",
                bgcolor: "#F8FAFC",
                minHeight: { xs: "52px", md: "40px" },
                flexShrink: 0,
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: { xs: "52px", md: "40px" },
                  fontSize: "12px",
                  fontWeight: 500,
                  px: { xs: 1, md: 1.5 },
                  color: "#64748B",
                  fontFamily: "Inter, sans-serif",
                  "&.Mui-selected": {
                    color: "#2563EB",
                    fontWeight: 600,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#2563EB",
                  height: "2px",
                },
              }}
            >
              {tabsData.map((tab, index) => (
                <Tab
                  key={index}
                  icon={
                    <TabIcon
                      icon={tab.icon}
                      bgColor={tab.bgColor}
                      color={tab.color}
                    />
                  }
                  iconPosition={isMobile ? "top" : "start"}
                  label={isMobile ? undefined : tab.label}
                  title={tab.label}
                  sx={isMobile ? { minWidth: 48, px: 1, py: 0.5, minHeight: "48px" } : {}}
                />
              ))}
            </Tabs>
            {/* Tab Content Area  */}
            <Box
              sx={{
                flex: { xs: "none", md: 1 },
                overflowY: { xs: "visible", md: "auto" },
                p: 1,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "4px",
                },
              }}
            >
              {activeTab === 0 && <CaseDetailOverview />}
              {activeTab === 1 && (
                <CaseDetailJudgementTab
                  searchTerm={searchTerm}
                  currentMatch={currentMatch}
                  setCurrentMatch={setCurrentMatch}
                  setTotalMatches={setTotalMatches}
                />
              )}
              {activeTab === 2 && (
                <CaseDetailStatutes
                  items={caseItem?.Statute_Collection || []}
                />
              )}
              {activeTab === 3 && <CaseDetailAdvocates />}
              {activeTab === 4 && (
                <CaseDetailSummary
                  searchTerm={searchTerm}
                  currentMatch={currentMatch}
                  setCurrentMatch={setCurrentMatch}
                  setTotalMatches={setTotalMatches}
                  summary={caseItem?.Detailed_Lawcase_Summary}
                />
              )}
              {activeTab === 5 && (
                <CaseDetailMaxims loading={loading} maxims={maxims} />
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseDetail;
